import React, { useState, useEffect, useRef, useMemo } from 'react';
import type { AccountUser, AccessPolicy, UserType, EmploymentType } from '../types';
import {
  GRADE_LABELS,
  isCompanyDomain,
  ACCESS_POLICY_LABELS,
  USER_TYPE_LABELS,
  AUTH_TYPE_LABELS,
  EMPLOYMENT_TYPE_LABELS,
} from '../types';
import {
  updateUserProfile,
  updateExtendedProfile,
  updateAccessPolicy,
  updateUserType,
  updateSingleAppRole,
  setAppDisabled,
  updateOrgFields,
  fetchAllUsers,
  type AppKey,
} from '../services/userService';
import { fetchAllUnits, type Unit } from '../services/unitsService';
import { disableUserAccount } from '../services/inviteService';
import { uploadAvatar, validateAvatarFile } from '../services/storageService';
import { fetchUserAuditLogs, type AuditLogEntry } from '../services/auditLogService';

// ── Types ────────────────────────────────────────────────────────────────

interface Props {
  user: AccountUser | null;
  onClose: () => void;
  onSaved: (updated: AccountUser) => void;
}

type Tab = 'overview' | 'access' | 'activity' | 'employment' | 'organization';

const TAB_LABELS: Record<Tab, string> = {
  overview:     'Overview',
  access:       'Access',
  activity:     'Activity',
  employment:   'Employment',
  organization: 'Organization',
};

const APP_LABELS: Record<AppKey, string> = {
  okr:    'OKR Tracker',
  epr:    'EPR Service',
  portal: 'Leadership Portal',
};

const APP_ROLE_OPTIONS: Record<AppKey, Array<{ value: string; label: string }>> = {
  okr: [
    { value: 'viewer', label: 'Viewer' },
    { value: 'member', label: 'Member' },
    { value: 'leader', label: 'Leader' },
    { value: 'admin',  label: 'Admin' },
  ],
  epr: [
    { value: 'employee',     label: 'Employee' },
    { value: 'manager',      label: 'Manager' },
    { value: 'hr_admin',     label: 'HR Admin' },
    { value: 'admin',        label: 'Admin' },
  ],
  portal: [
    { value: 'viewer', label: 'Viewer' },
    { value: 'leader', label: 'Leader' },
    { value: 'admin',  label: 'Admin' },
  ],
};

// ── Small primitives ─────────────────────────────────────────────────────

function StatusDot({ tone, className = '' }: { tone: 'active' | 'inactive' | 'pending' | 'blocked'; className?: string }) {
  const color =
    tone === 'active'   ? 'bg-emerald-500'
  : tone === 'pending'  ? 'bg-amber-500'
  : tone === 'blocked'  ? 'bg-stone-300'
  : 'bg-red-500';
  return <span className={`inline-block w-1.5 h-1.5 rounded-full ${color} ${className}`} />;
}

const sectionLabel  = 'text-[11px] font-medium uppercase tracking-wider text-stone-400';
const inputBase     = 'w-full rounded-md border border-stone-300 px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-900 bg-white';
const ghostButton   = 'inline-flex items-center gap-1.5 text-xs font-medium text-stone-700 hover:text-stone-900 hover:bg-stone-100 rounded-md px-2.5 py-1.5 transition';
const primaryButton = 'inline-flex items-center gap-1.5 text-xs font-medium text-white bg-stone-900 hover:bg-stone-800 rounded-md px-2.5 py-1.5 transition disabled:opacity-50';

// ── Inline editable row (text input) ─────────────────────────────────────

function InlineText({
  label, value, placeholder, onSave, type = 'text', multiline = false, suffix,
}: {
  label: string;
  value: string;
  placeholder?: string;
  onSave: (next: string) => Promise<void>;
  type?: 'text' | 'tel' | 'date' | 'email';
  multiline?: boolean;
  suffix?: React.ReactNode;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => { setDraft(value); }, [value]);

  const enter = () => { setEditing(true); setDraft(value); setError(''); };
  const cancel = () => { setEditing(false); setError(''); setDraft(value); };
  const save = async () => {
    if (draft === value) { setEditing(false); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e: any) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="py-3 group">
      <div className={sectionLabel}>{label}</div>
      {editing ? (
        <div className="mt-2 space-y-2">
          {multiline ? (
            <textarea
              autoFocus
              value={draft}
              onChange={e => setDraft(e.target.value)}
              rows={3}
              className={`${inputBase} resize-none`}
              placeholder={placeholder}
              onKeyDown={e => { if (e.key === 'Escape') cancel(); }}
            />
          ) : (
            <input
              autoFocus
              type={type}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              className={inputBase}
              placeholder={placeholder}
              onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
            />
          )}
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={saving} className={primaryButton}>{saving ? 'Saving…' : 'Save'}</button>
            <button onClick={cancel} disabled={saving} className={ghostButton}>Cancel</button>
            {error && <span className="text-xs text-red-600">{error}</span>}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={enter}
          className="mt-1 w-full text-left text-sm text-stone-900 hover:text-stone-900 rounded-md -mx-2 px-2 py-1 hover:bg-stone-50 transition flex items-center justify-between"
        >
          <span className={value ? '' : 'text-stone-400'}>
            {value || placeholder || '—'}
            {value && suffix ? <span className="text-stone-400 font-normal"> · {suffix}</span> : null}
          </span>
          <span className="material-symbols-rounded text-stone-300 opacity-0 group-hover:opacity-100 transition" style={{ fontSize: 14 }}>edit</span>
        </button>
      )}
    </div>
  );
}

// ── Inline editable row (select) ─────────────────────────────────────────

function InlineSelect({
  label, value, options, onSave, placeholder = '— Not set —',
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onSave: (next: string) => Promise<void>;
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => { setDraft(value); }, [value]);

  const enter = () => { setEditing(true); setDraft(value); setError(''); };
  const cancel = () => { setEditing(false); setError(''); setDraft(value); };
  const save = async () => {
    if (draft === value) { setEditing(false); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e: any) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const currentLabel = options.find(o => o.value === value)?.label;

  return (
    <div className="py-3 group">
      <div className={sectionLabel}>{label}</div>
      {editing ? (
        <div className="mt-2 space-y-2">
          <select
            autoFocus
            value={draft}
            onChange={e => setDraft(e.target.value)}
            className={inputBase}
          >
            <option value="">{placeholder}</option>
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={saving} className={primaryButton}>{saving ? 'Saving…' : 'Save'}</button>
            <button onClick={cancel} disabled={saving} className={ghostButton}>Cancel</button>
            {error && <span className="text-xs text-red-600">{error}</span>}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={enter}
          className="mt-1 w-full text-left text-sm text-stone-900 rounded-md -mx-2 px-2 py-1 hover:bg-stone-50 transition flex items-center justify-between"
        >
          <span className={currentLabel ? '' : 'text-stone-400'}>{currentLabel || placeholder}</span>
          <span className="material-symbols-rounded text-stone-300 opacity-0 group-hover:opacity-100 transition" style={{ fontSize: 14 }}>edit</span>
        </button>
      )}
    </div>
  );
}

// ── Read-only row (display only, no edit) ────────────────────────────────

function ReadField({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="py-3">
      <div className={sectionLabel}>{label}</div>
      <div className="mt-1 text-sm text-stone-900">{value || <span className="text-stone-400">—</span>}</div>
      {hint && <div className="mt-1 text-xs text-stone-400">{hint}</div>}
    </div>
  );
}

// ── App access row (apps matrix in Access tab) ───────────────────────────

function AppRow({
  app, role, blocked, onRoleChange, onBlockToggle,
}: {
  app: AppKey;
  role: string | undefined;
  blocked: boolean;
  onRoleChange: (next: string) => Promise<void>;
  onBlockToggle: () => Promise<void>;
}) {
  const [editingRole, setEditingRole] = useState(false);
  const [draftRole, setDraftRole]     = useState(role || '');
  const [saving, setSaving]           = useState(false);

  useEffect(() => { setDraftRole(role || ''); }, [role]);

  const options = APP_ROLE_OPTIONS[app];
  const roleLabel = options.find(o => o.value === role)?.label;

  const handleRoleSave = async () => {
    if (draftRole === (role || '')) { setEditingRole(false); return; }
    setSaving(true);
    try {
      await onRoleChange(draftRole);
      setEditingRole(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="py-3">
      <div className="flex items-center justify-between gap-3">
        {/* App name + status dot */}
        <div className="flex items-center gap-2.5 min-w-0">
          <StatusDot tone={blocked || !role ? 'blocked' : 'active'} />
          <span className="text-sm text-stone-900 truncate">{APP_LABELS[app]}</span>
        </div>

        {/* Role picker + block toggle */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {editingRole ? (
            <div className="flex items-center gap-1.5">
              <select
                autoFocus
                value={draftRole}
                onChange={e => setDraftRole(e.target.value)}
                className="text-xs border border-stone-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10"
              >
                <option value="">No role</option>
                {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button onClick={handleRoleSave} disabled={saving} className="text-xs font-medium text-stone-900 hover:text-stone-700 disabled:opacity-50">
                {saving ? '…' : 'Save'}
              </button>
              <button onClick={() => { setEditingRole(false); setDraftRole(role || ''); }} className="text-xs text-stone-500 hover:text-stone-700">Cancel</button>
            </div>
          ) : (
            <button
              onClick={() => setEditingRole(true)}
              className="text-xs text-stone-700 hover:text-stone-900 rounded-md hover:bg-stone-100 px-2 py-1 transition"
            >
              {roleLabel || <span className="text-stone-400">No role</span>}
            </button>
          )}

          <button
            onClick={onBlockToggle}
            className={`text-xs font-medium px-2 py-1 rounded-md transition ${
              blocked ? 'text-red-700 hover:bg-red-50' : 'text-stone-500 hover:text-stone-900 hover:bg-stone-100'
            }`}
            title={blocked ? 'Click to unblock login' : 'Click to block login'}
          >
            {blocked ? 'Blocked' : 'Allowed'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Activity timeline ────────────────────────────────────────────────────

const APP_LABEL_FROM_FIELD: Record<string, string> = {
  'appRoles.okr':    'OKR Tracker role',
  'appRoles.epr':    'EPR role',
  'appRoles.portal': 'Leadership Portal role',
  'appDisabled.okr':    'OKR Tracker',
  'appDisabled.epr':    'EPR',
  'appDisabled.portal': 'Leadership Portal',
};

function describeAction(e: AuditLogEntry): string {
  const af = String(e.after ?? '');
  switch (e.action) {
    case 'invited':               return 'Account created';
    case 'suspended':             return 'Suspended account';
    case 'reactivated':           return 'Reactivated account';
    case 'access_policy_changed': return `Set access policy to ${af.replace('_', ' ')}`;
    case 'role_changed':          return `Changed primary role to ${af || '—'}`;
    case 'app_role_changed':      return `Set ${APP_LABEL_FROM_FIELD[e.field || ''] || e.field} to ${af || 'none'}`;
    case 'app_blocked':           return `Blocked ${APP_LABEL_FROM_FIELD[e.field || ''] || 'app'} login`;
    case 'app_allowed':           return `Allowed ${APP_LABEL_FROM_FIELD[e.field || ''] || 'app'} login`;
    case 'profile_updated':       return `Updated profile (${e.field || 'fields'})`;
    case 'deleted':               return 'Deleted account';
    default:                      return e.action.replace(/_/g, ' ');
  }
}

function timestampToMs(ts: any): number | null {
  if (!ts) return null;
  if (typeof ts === 'object' && ts.toMillis) return ts.toMillis();
  if (typeof ts === 'number') return ts;
  const v = new Date(ts).getTime();
  return Number.isFinite(v) ? v : null;
}

function relTime(ts: any): string {
  const ms = timestampToMs(ts);
  if (ms === null) return '—';
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ms).toLocaleDateString();
}

function ActivityTimeline({ userId, actors }: {
  userId: string;
  actors: Array<{ id: string; name: string }>;
}) {
  const [logs, setLogs]     = useState<AuditLogEntry[] | null>(null);
  const [error, setError]   = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLogs(null);
    setError(null);
    fetchUserAuditLogs(userId)
      .then(rows => { if (!cancelled) setLogs(rows); })
      .catch(e => { if (!cancelled) setError(e?.message || 'Failed to load activity'); });
    return () => { cancelled = true; };
  }, [userId]);

  if (error) {
    return <div className="py-8 text-sm text-stone-500">Couldn't load activity. {error}</div>;
  }

  if (logs === null) {
    return (
      <div className="py-6 space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex gap-3 animate-pulse">
            <div className="w-1.5 h-1.5 rounded-full bg-stone-100 mt-1.5 flex-shrink-0" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3 bg-stone-100 rounded w-44" />
              <div className="h-2.5 bg-stone-50 rounded w-32" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="py-14 flex flex-col items-center text-center px-6">
        <p className="text-sm font-medium text-stone-700">No activity yet</p>
        <p className="mt-1 text-xs text-stone-500 max-w-xs">
          Changes to roles, access, and profile fields will appear here.
        </p>
      </div>
    );
  }

  return (
    <ol className="py-4 space-y-4">
      {logs.map(log => {
        const actor = actors.find(a => a.id === log.performedBy);
        const actorLabel = log.performedBy === 'system' ? 'System' : actor?.name || '—';
        return (
          <li key={log.id} className="flex gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-stone-300 mt-2 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-stone-900">{describeAction(log)}</p>
              <p className="text-xs text-stone-500 mt-0.5">
                by {actorLabel} · {relTime(log.timestamp)}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ── Main panel ───────────────────────────────────────────────────────────

const UserPanel: React.FC<Props> = ({ user, onClose, onSaved }) => {
  const [tab, setTab]   = useState<Tab>('overview');
  const [units, setUnits]   = useState<Unit[]>([]);
  const [allUsers, setAll]  = useState<Array<{ id: string; name: string; email: string; directManagerId?: string }>>([]);
  const [busy, setBusy]     = useState(false);
  const [hrisOpen, setHris] = useState(false);
  const [localPhoto, setLocalPhoto] = useState<string | null>(null);
  const [avatarErr, setAvatarErr]   = useState('');
  const [avatarBusy, setAvatarBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!user) return;
    setTab('overview');
    setHris(false);
    setLocalPhoto(null);
    setAvatarErr('');
    void fetchAllUnits().then(setUnits).catch(() => {});
    void fetchAllUsers().then(list =>
      setAll(list.map(u => ({ id: u.id, name: u.name, email: u.email, directManagerId: (u as any).directManagerId }))),
    ).catch(() => {});
  }, [user?.uid]);

  // ── Derived via hooks ───────────────────────────────────────────────────
  // NOTE: these useMemo calls must run on EVERY render, so they live ABOVE the
  // `if (!user) return null` guard below. Moving them after the early return
  // changes the hook count between renders → React error #310.
  const subtitle = useMemo(() => {
    if (!user) return '';
    const parts: string[] = [];
    if (user.jobTitle) parts.push(user.jobTitle);
    const apps = Object.entries(user.appRoles || {}).filter(([, r]) => !!r).map(([k]) => APP_LABELS[k as AppKey]).join(' + ');
    if (apps) parts.push(apps);
    return parts.join(' · ');
  }, [user?.jobTitle, user?.appRoles]);

  // Format lastSignInAt (Firestore Timestamp | number | string) → "2h ago"
  const lastActiveLabel = useMemo(() => {
    const ts = (user as any)?.lastSignInAt;
    if (!ts) return null;
    const ms = typeof ts === 'object' && ts.toMillis ? ts.toMillis() : typeof ts === 'number' ? ts : new Date(ts).getTime();
    if (!Number.isFinite(ms)) return null;
    const diff = Date.now() - ms;
    if (diff < 60_000) return 'just now';
    const m = Math.floor(diff / 60_000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(ms).toLocaleDateString();
  }, [(user as any)?.lastSignInAt]);

  const manager      = useMemo(() => allUsers.find(u => u.id === (user as any)?.directManagerId), [allUsers, user]);
  const reportChain  = useMemo(() => {
    const chain: typeof allUsers = [];
    const seen = new Set<string>();
    let cur = manager;
    while (cur && !seen.has(cur.id)) {
      chain.push(cur);
      seen.add(cur.id);
      cur = allUsers.find(u => u.id === cur!.directManagerId);
    }
    return chain;
  }, [manager, allUsers]);
  const directReports = useMemo(
    () => (user ? allUsers.filter(u => u.directManagerId === user.uid) : []),
    [allUsers, user?.uid],
  );

  if (!user) return null;

  // ── Derived (plain, after the null-guard) ────────────────────────────────
  const isActive   = user.isActive !== false;
  const authType   = user.auth_type || (isCompanyDomain(user.email) ? 'microsoft_sso' : 'local_invited');
  const userType   = user.user_type || (isCompanyDomain(user.email) ? 'employee' : 'external_worker');
  const policy     = user.access_policy || 'full_access';
  const photo      = localPhoto || user.photoURL || user.avatar || null;
  const initial    = (user.name || user.displayName || '?')[0]?.toUpperCase();
  const tone = !isActive ? 'inactive' : user.status === 'invited' ? 'pending' : 'active';
  const toneLabel = !isActive ? 'Suspended' : user.status === 'invited' ? 'Pending invite' : 'Active';

  // ── Mutations (auto-save inline) ──────────────────────────────────────
  const wrapSave = async <T,>(fn: () => Promise<T>, patch: Partial<AccountUser>) => {
    setBusy(true);
    try {
      await fn();
      onSaved({ ...user, ...patch });
    } finally {
      setBusy(false);
    }
  };

  const handleAvatarClick = () => fileInputRef.current?.click();
  const handleAvatarFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarErr('');
    const v = validateAvatarFile(file);
    if (v) { setAvatarErr(v); return; }
    setAvatarBusy(true);
    try {
      const url = await uploadAvatar(user.uid, file);
      setLocalPhoto(url);
      onSaved({ ...user, photoURL: url });
    } catch (err: any) {
      setAvatarErr(err.message || 'Upload failed');
    } finally {
      setAvatarBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSuspendToggle = async () => {
    const next = !isActive ? false : true; // next disabled value
    await wrapSave(
      () => disableUserAccount(user.uid, next),
      { isActive: !next, status: !next ? 'active' : 'suspended' },
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <>
      <div className="fixed inset-0 bg-stone-900/20 z-40 backdrop-blur-[1px]" onClick={onClose} />

      <aside className="fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-xl z-50 flex flex-col">

        {/* ── Hero ────────────────────────────────────────────────────── */}
        <div className="flex-shrink-0 px-6 pt-6 pb-5">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              {/* Avatar */}
              <button
                type="button"
                onClick={handleAvatarClick}
                disabled={avatarBusy}
                className="relative w-16 h-16 rounded-full overflow-hidden group focus:outline-none ring-1 ring-stone-100 hover:ring-stone-200 transition"
                title="Change photo"
              >
                {photo
                  ? <img src={photo} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full bg-stone-100 flex items-center justify-center text-stone-600 font-semibold text-2xl">{initial}</div>
                }
                <div className="absolute inset-0 bg-stone-900/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  {avatarBusy
                    ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <span className="material-symbols-rounded text-white" style={{ fontSize: 18 }}>photo_camera</span>}
                </div>
              </button>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleAvatarFile} />

              {/* Name + subtitle */}
              <h2 className="mt-3 text-xl font-semibold text-stone-900 tracking-tight truncate">{user.name || user.displayName}</h2>
              {subtitle && <p className="mt-0.5 text-sm text-stone-500 truncate">{subtitle}</p>}

              {/* Status */}
              <div className="mt-2 flex items-center gap-2 text-sm">
                <StatusDot tone={tone} />
                <span className="text-stone-600">{toneLabel}</span>
                {user.grade && <span className="text-stone-300">·</span>}
                {user.grade && <span className="text-stone-500">{user.grade}</span>}
              </div>
            </div>

            <button onClick={onClose} className="text-stone-400 hover:text-stone-700 transition flex-shrink-0 -mt-1 -mr-1 p-1.5 rounded-md hover:bg-stone-100">
              <span className="material-symbols-rounded" style={{ fontSize: 20 }}>close</span>
            </button>
          </div>

          {/* Primary actions */}
          <div className="mt-5 flex items-center gap-2">
            {isActive ? (
              <button
                onClick={handleSuspendToggle}
                disabled={busy}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-red-700 hover:bg-red-50 rounded-md px-3 py-1.5 transition disabled:opacity-50"
              >
                <span className="material-symbols-rounded" style={{ fontSize: 16 }}>block</span>
                Suspend
              </button>
            ) : (
              <button
                onClick={handleSuspendToggle}
                disabled={busy}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50 rounded-md px-3 py-1.5 transition disabled:opacity-50"
              >
                <span className="material-symbols-rounded" style={{ fontSize: 16 }}>play_arrow</span>
                Reactivate
              </button>
            )}
          </div>

          {avatarErr && <p className="mt-3 text-xs text-red-600">{avatarErr}</p>}
        </div>

        {/* ── Tabs ────────────────────────────────────────────────────── */}
        <div className="flex-shrink-0 px-6 border-b border-stone-100">
          <div className="flex gap-5 overflow-x-auto">
            {(Object.keys(TAB_LABELS) as Tab[]).map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`relative -mb-px py-3 text-sm font-medium whitespace-nowrap transition ${
                  tab === t
                    ? 'text-stone-900'
                    : 'text-stone-500 hover:text-stone-700'
                }`}
              >
                {TAB_LABELS[t]}
                {tab === t && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-stone-900 rounded-full" />}
              </button>
            ))}
          </div>
        </div>

        {/* ── Body ────────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-6">

          {/* OVERVIEW */}
          {tab === 'overview' && (
            <div className="divide-y divide-stone-100">
              <ReadField
                label="Email"
                value={user.email}
                hint={AUTH_TYPE_LABELS[authType] || authType}
              />
              <InlineText
                label="Job Title"
                value={user.jobTitle || ''}
                placeholder="e.g. Marketing Manager"
                onSave={async v => wrapSave(
                  () => updateUserProfile(user.uid, { jobTitle: v.trim() || undefined }),
                  { jobTitle: v.trim() },
                )}
              />
              <InlineText
                label="Team"
                value={user.department || ''}
                placeholder="e.g. Marketing"
                onSave={async v => wrapSave(
                  () => updateExtendedProfile(user.uid, { department: v.trim() || undefined }),
                  { department: v.trim() },
                )}
              />
              <InlineSelect
                label="Manager"
                value={(user as any).directManagerId || ''}
                options={allUsers
                  .filter(u => u.id !== user.uid)
                  .map(u => ({ value: u.id, label: u.name }))}
                onSave={async v => wrapSave(
                  () => updateOrgFields(user.uid, { directManagerId: v || null }),
                  { ...(user as any), directManagerId: v || null },
                )}
              />
              <ReadField label="Last active" value={lastActiveLabel} hint={lastActiveLabel ? undefined : 'No sign-in recorded yet.'} />
            </div>
          )}

          {/* ACCESS */}
          {tab === 'access' && (
            <div className="divide-y divide-stone-100">
              <InlineSelect
                label="Access policy"
                value={policy}
                options={(['full_access', 'limited_access', 'no_access'] as AccessPolicy[]).map(p => ({
                  value: p, label: ACCESS_POLICY_LABELS[p],
                }))}
                onSave={async v => wrapSave(
                  () => updateAccessPolicy(user.uid, v as AccessPolicy),
                  { access_policy: v as AccessPolicy },
                )}
              />
              <InlineSelect
                label="User type"
                value={userType}
                options={Object.entries(USER_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }))}
                onSave={async v => wrapSave(
                  () => updateUserType(user.uid, v as UserType),
                  { user_type: v as UserType },
                )}
              />

              {/* Apps matrix */}
              <div className="py-3">
                <div className={sectionLabel}>Apps</div>
                <div className="mt-1 divide-y divide-stone-100">
                  {(['okr', 'epr', 'portal'] as AppKey[]).map(app => (
                    <AppRow
                      key={app}
                      app={app}
                      role={user.appRoles?.[app]}
                      blocked={!!user.appDisabled?.[app]}
                      onRoleChange={async v => wrapSave(
                        () => updateSingleAppRole(user.uid, app, v),
                        { appRoles: { ...(user.appRoles || {}), [app]: v } },
                      )}
                      onBlockToggle={async () => {
                        const next = !user.appDisabled?.[app];
                        await wrapSave(
                          () => setAppDisabled(user.uid, app, next),
                          { appDisabled: { ...(user.appDisabled || {}), [app]: next } },
                        );
                      }}
                    />
                  ))}
                </div>
              </div>

              <ReadField
                label="Sign-in method"
                value={AUTH_TYPE_LABELS[authType] || authType}
                hint={authType === 'microsoft_sso' ? 'Managed by Microsoft 365 — MFA enforced at tenant level.' : 'Local password — MFA not enforced.'}
              />
              <ReadField label="Active sessions" value={null} hint="Session listing requires server-side fetch — coming later." />
            </div>
          )}

          {/* ACTIVITY */}
          {tab === 'activity' && (
            <ActivityTimeline userId={user.uid} actors={allUsers} />
          )}

          {/* EMPLOYMENT */}
          {tab === 'employment' && (
            <div className="divide-y divide-stone-100">
              <InlineText
                label="Job title"
                value={user.jobTitle || ''}
                placeholder="e.g. Marketing Manager"
                onSave={async v => wrapSave(
                  () => updateUserProfile(user.uid, { jobTitle: v.trim() || undefined }),
                  { jobTitle: v.trim() },
                )}
              />
              <InlineSelect
                label="Grade"
                value={user.grade || ''}
                options={Object.entries(GRADE_LABELS).map(([v, l]) => ({ value: v, label: l }))}
                onSave={async v => wrapSave(
                  () => updateUserProfile(user.uid, { grade: v || undefined }),
                  { grade: v },
                )}
              />
              <InlineText
                label="Department"
                value={user.department || ''}
                placeholder="e.g. Marketing"
                onSave={async v => wrapSave(
                  () => updateExtendedProfile(user.uid, { department: v.trim() || undefined }),
                  { department: v.trim() },
                )}
              />
              <InlineText
                label="Start date"
                value={user.startDate || ''}
                type="date"
                onSave={async v => wrapSave(
                  () => updateExtendedProfile(user.uid, { startDate: v || undefined }),
                  { startDate: v },
                )}
              />
              <InlineText
                label="Site"
                value={user.site || ''}
                placeholder="e.g. Lombok Office"
                onSave={async v => wrapSave(
                  () => updateExtendedProfile(user.uid, { site: v.trim() || undefined }),
                  { site: v.trim() },
                )}
              />
              <InlineSelect
                label="Employment type"
                value={user.employment_type || ''}
                options={Object.entries(EMPLOYMENT_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }))}
                onSave={async v => wrapSave(
                  () => updateExtendedProfile(user.uid, { employment_type: (v as EmploymentType) || undefined }),
                  { employment_type: v as EmploymentType },
                )}
              />

              {/* Progressive disclosure for less-used fields */}
              <div className="py-3">
                <button
                  onClick={() => setHris(o => !o)}
                  className="text-xs font-medium text-stone-500 hover:text-stone-900 transition flex items-center gap-1"
                >
                  <span className="material-symbols-rounded transition-transform" style={{ fontSize: 14, transform: hrisOpen ? 'rotate(90deg)' : 'none' }}>chevron_right</span>
                  {hrisOpen ? 'Hide' : 'Show'} HRIS extras
                </button>
                {hrisOpen && (
                  <div className="mt-2 divide-y divide-stone-100">
                    <InlineText
                      label="Employee ID"
                      value={user.employee_id || ''}
                      placeholder="e.g. EMP-0042"
                      onSave={async v => wrapSave(
                        () => updateExtendedProfile(user.uid, { employee_id: v.trim() || undefined }),
                        { employee_id: v.trim() },
                      )}
                    />
                    <InlineText
                      label="Phone"
                      value={user.phone || ''}
                      type="tel"
                      placeholder="+62 812 …"
                      onSave={async v => wrapSave(
                        () => updateExtendedProfile(user.uid, { phone: v.trim() || undefined }),
                        { phone: v.trim() },
                      )}
                    />
                    <InlineText
                      label="Nationality"
                      value={user.nationality || ''}
                      placeholder="e.g. Indonesian"
                      onSave={async v => wrapSave(
                        () => updateExtendedProfile(user.uid, { nationality: v.trim() || undefined }),
                        { nationality: v.trim() },
                      )}
                    />
                    <InlineText
                      label="Emergency contact"
                      value={user.emergencyContact || ''}
                      placeholder="Name & phone number"
                      onSave={async v => wrapSave(
                        () => updateExtendedProfile(user.uid, { emergencyContact: v.trim() || undefined }),
                        { emergencyContact: v.trim() },
                      )}
                    />
                    <InlineText
                      label="Bio"
                      value={user.bio || ''}
                      multiline
                      placeholder="Short bio or notes about this employee…"
                      onSave={async v => wrapSave(
                        () => updateExtendedProfile(user.uid, { bio: v.trim() || undefined }),
                        { bio: v.trim() },
                      )}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ORGANIZATION */}
          {tab === 'organization' && (
            <div className="divide-y divide-stone-100">
              <div className="py-3">
                <div className={sectionLabel}>Reports to</div>
                {manager ? (
                  <div className="mt-2 flex items-center gap-2 flex-wrap text-sm">
                    {[...reportChain].reverse().map((m, i, arr) => (
                      <React.Fragment key={m.id}>
                        <span className="text-stone-900">{m.name}</span>
                        {i < arr.length - 1 && <span className="material-symbols-rounded text-stone-300" style={{ fontSize: 14 }}>chevron_right</span>}
                      </React.Fragment>
                    ))}
                  </div>
                ) : (
                  <div className="mt-1 text-sm text-stone-400">No manager assigned</div>
                )}
              </div>

              <InlineSelect
                label="Unit"
                value={user.unitId || ''}
                options={units.map(u => ({ value: u.id, label: u.name }))}
                placeholder="No unit assigned"
                onSave={async v => wrapSave(
                  () => updateOrgFields(user.uid, {
                    unitId: v || null,
                    unitName: v ? (units.find(u => u.id === v)?.name ?? null) : null,
                  }),
                  { unitId: v, unitName: v ? (units.find(u => u.id === v)?.name ?? undefined) : undefined },
                )}
              />

              <div className="py-3">
                <div className="flex items-center justify-between">
                  <span className={sectionLabel}>Direct reports</span>
                  <span className="text-[11px] text-stone-400 tabular-nums">{directReports.length}</span>
                </div>
                {directReports.length === 0 ? (
                  <div className="mt-1 text-sm text-stone-400">No direct reports</div>
                ) : (
                  <ul className="mt-2 space-y-1.5">
                    {directReports.map(r => (
                      <li key={r.id} className="flex items-center gap-2.5 text-sm text-stone-900">
                        <div className="w-6 h-6 rounded-full bg-stone-100 flex items-center justify-center text-xs font-medium text-stone-600">
                          {r.name[0]?.toUpperCase()}
                        </div>
                        <span className="truncate">{r.name}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="py-4">
                <p className="text-xs text-stone-400">
                  Full org chart canvas (zoom, pan, focus mode) coming in the Organization page.
                </p>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default UserPanel;
